import { Router, NavigationEnd, NavigationStart, GuardsCheckEnd, GuardsCheckStart, NavigationCancel } from '@angular/router';
import {Component, OnInit, TemplateRef, ViewChild, SecurityContext, HostListener} from '@angular/core';
import { LoginService } from './shared-modules/login/login.service';
import { LoginSettings } from './shared-modules/login/loginSetting.model';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from './shared-components/shared.service';
import { Subscription } from '../../node_modules/rxjs';
import { UserService } from './shared-components/user/user.service';
import { MatSidenav } from '@angular/material/sidenav';
import { InactivityTimeoutComponent } from './shared-modules/login/inactivity-timeout/inactivity-timeout.component';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { AppInsights } from 'applicationinsights-js';
import * as moment from 'moment';
import Quill from 'quill';
import { DomSanitizer } from '@angular/platform-browser';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
    title = 'Tyler SIS';
    LoginSettings: LoginSettings;
    showAppHeader = true;
    showAppHeaderSubscription: Subscription;
    showLoadingImage = true;
    public usertype = 0;
    currentUserSubscription: Subscription;
    loginSubscription: Subscription;
    appTimeout: number;
    idleTimeout: any;
    inactivityDialog: MatDialogRef<InactivityTimeoutComponent>;
    @ViewChild('snav') sidenav: MatSidenav;
    startViewTime;

    // TODO: make a district setting for this
    tracking = false;

    constructor(
        private userService: UserService,
        private loginService: LoginService,
        private translateService: TranslateService,
        private sharedService: SharedService,
        private router: Router,
        private dialog: MatDialog,
        private sanitizer: DomSanitizer
    ) {
        this.translateService.setDefaultLang('en');
        this.translateService.use('en');
        this.showAppHeader = sharedService.showAppHeader;
        this.showAppHeaderSubscription = sharedService.observableShowAppHeaderStream$.subscribe(
            showHeader => {
                this.showAppHeader = showHeader;
            }
        );

        this.loginSubscription = this.loginService.observableLoginStream$.subscribe(() => {
            this.setTimeoutSettings();
            this.resetIdleTimeout();
        });
    }

    get showFilter(): boolean {
        return this.sharedService.showFilter;
    }

    get filterTemplate(): TemplateRef<any> {
        return this.sharedService.filterTemplate;
    }

    ngOnInit() {
        this.getDistrictSettings();
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart || event instanceof GuardsCheckEnd) {
                this.sharedService.updateShowSpinner(true);
            }
            if (event instanceof NavigationCancel || event instanceof GuardsCheckStart) {
                this.sharedService.updateShowSpinner(false);
            }
            if (event instanceof NavigationEnd) {
                this.startViewTime = moment();
                this.sharedService.updateShowSpinner(false);
            }
        });

        this.addOverlayClickListener();
        this.currentUserSubscription = this.userService.observableCurrentUserStream$
            .subscribe(data => {
                this.usertype = data.UserType;
            });
        this.configureLinksForQuill();
    }

    addOverlayClickListener() {
        document.addEventListener('click', function (e) {
            if (e.target['className'] != undefined) {
                const tempString: any = e.target['className'];  // sometimes e.target is an SVGAnimatedString which does not have a method "includes"
                if ((tempString.includes && tempString.includes('cdk-overlay-backdrop') && !tempString.includes('cdk-overlay-backdrop-showing'))) {
                    const ev = document.createEvent('MouseEvent');
                    const el = document.elementFromPoint(e.x, e.y);
                    ev.initMouseEvent(
                        'click',
                        true /* bubble */, true /* cancelable */,
                        window, null,
                        e.x, e.y, 0, 0, /* coordinates */
                        false, false, false, false, /* modifier keys */
                        0 /*left*/, null
                    );
                    el.dispatchEvent(ev);
                }
            }
        });
    }

    getDistrictSettings(): void {
        this.loginService.getDistrictSettings().subscribe(data => {
            this.LoginSettings = data;
            this.loginService.LoginSettings = data;
            this.showLoadingImage = false;
            this.appTimeout = this.LoginSettings.TimeoutTime - 1;
            this.tracking = data.ActivateApplicationMonitoring;

            // Initializing App Insights
            if (this.tracking) {
                AppInsights.downloadAndSetup({
                    instrumentationKey: 'b1cb9a66-3819-467f-960c-1d9be59bc330'
                });
                this.router.events.subscribe(event => {
                    if (event instanceof NavigationStart && this.startViewTime && this.tracking) {
                        const urlParts = window.location.href.split('/');
                        const pageName = urlParts[urlParts.length - 1];
                        AppInsights.trackPageView(
                            pageName, /* (optional) page name */
                            window.location.href.toLowerCase(), /* (optional) page url if available */
                            { }, /* (optional) dimension dictionary */
                            { }, /* (optional) metric dictionary */
                            moment().diff(this.startViewTime) /* page view duration in milliseconds */
                        );
                    }
                });
            } else {
                AppInsights.disableTelemetry = true;
            }
        });
    }

    setCurrentComponent(component) {
        this.sharedService.currentComponent = component;
    }

    closeSideNav() {
        this.sidenav.close();
    }

    setTimeoutSettings() {
        ['click', 'touchstart', 'mousemove'].forEach(evt =>
            document.addEventListener(evt, this.resetIdleTimeout.bind(this), false)
        );
    }

    resetIdleTimeout() {
        if (this.idleTimeout) {
            clearTimeout(this.idleTimeout);
        }
        this.idleTimeout = setTimeout(() => this.showTimeoutModal(), this.appTimeout * 60000);
    }

    showTimeoutModal() {
        this.inactivityDialog = this.dialog.open(InactivityTimeoutComponent, { panelClass: 'tyl-mat-dialog' });
        this.inactivityDialog.componentInstance.inactiveTime = this.appTimeout;
        this.inactivityDialog.componentInstance.resetTimeout.subscribe(() => {
            this.resetIdleTimeout();
        });
    }

    configureLinksForQuill() {
        const Link = Quill.import('formats/link');
        Link.sanitize = url => {
            if (!url.startsWith('http:') && !url.startsWith('https:') && !url.startsWith('mailto:') && !url.startsWith('tel:')) {
                url = 'http://' + url;
            }
            return this.sanitizer.sanitize(SecurityContext.HTML, url);
        };
    }

    @HostListener('document:keydown', ['$event'])
    onEnterKeyDown(event: KeyboardEvent) {
        // PRE: The user presses a key
        // POST: Performs actions based on that key

        // CTRL + SHIFT + G generates error for testing
        if (event.key.toLowerCase() == 'g' && event.ctrlKey && event.shiftKey) {
            this.sharedService.generateError().subscribe();
        }
    }
}
